import { getCookie, truncateString } from './utils.js';
import { addAlert } from './alerts.js';
import { initializeSelect } from './manuals/form';
import { prompt } from './prompts.js';
import { initializePings, updatePing, handlePingSeen } from './pings';
import { showContextMenu } from './contextMenu';

const UPLOAD_URL = '/api/files/upload_multiple/';
const SORT_STORAGE_KEY = 'fileSortOrder';
const PLACEHOLDER_ID_SEGMENT = '/0/';

// --- Helper Utilities ---

/**
 * Dispatches events to notify other parts of the app to refresh their content.
 */
function dispatchGalleryUpdateEvents() {
    document.dispatchEvent(new CustomEvent('photosUpdatedInModal'));
    document.dispatchEvent(new CustomEvent('photosUpdatedEvent'));
}

/**
 * Shows a success alert.
 */
function notifySuccess(message) {
    addAlert('success', message);
}

/**
 * Shows an error alert.
 */
function notifyError(message) {
    addAlert('error', message);
}

/**
 * Handles the API call to update photo properties (e.g., is_main, is_offer).
 * @param {string} componentId - The ID of the component.
 * @param {Array<object>} updates - An array of update objects for the photos.
 */
async function updatePhotos(componentId, updates) {
    const url = `/api/components/${componentId}/photos/update/`;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCookie('csrftoken'),
            },
            body: JSON.stringify(updates),
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
                errorData.message || gettext('Błąd aktualizacji zdjęcia.'),
            );
        }
        notifySuccess(gettext('Status zdjęcia zaktualizowany.'));
        dispatchGalleryUpdateEvents();
    } catch (error) {
        console.error('Error updating photos:', error);
        notifyError(error.message);
    }
}

// --- File Gallery Class ---

class FileGallery {
    #element;
    #config;
    #elements;
    #eventHandlers = {};

    constructor(element) {
        this.#element = element;
        this.#config = {};
        this.#elements = this.#selectDOMElements();
        this.#init();
    }

    async #init() {
        this.#destroyChoices();
        initializeSelect(this.#elements.sortSelect, { position: 'top' });
        this.#attachEventListeners();
        await this.loadData();
    }

    /**
     * Reads the current configuration from the element's data attributes.
     * This method centralizes the logic for updating the internal config.
     * @private
     */
    #_refreshConfig() {
        this.#config = {
            getUrl: this.#element.dataset.getUrl,
            addUrl: this.#element.dataset.addUrl,
            removeUrl: this.#element.dataset.removeUrl,
            filesField: this.#element.dataset.filesField || 'documents',
            pingButtonId: this.#element.dataset.pingButtonId,
        };
    }

    #selectDOMElements() {
        return {
            fileListContainer: this.#element.querySelector('.file-list'),
            addButton: this.#element.querySelector('.add-button'),
            fileUploadInput: this.#element.querySelector('.file-upload-input'),
            deleteButton: this.#element.querySelector('.delete-button'),
            selectAllCheckbox: this.#element.querySelector(
                '.select-all-checkbox',
            ),
            sortSelect: this.#element.querySelector('.sort-select'),
        };
    }

    #destroyChoices() {
        if (this.#elements.sortSelect?._choices) {
            this.#elements.sortSelect._choices.destroy();
            this.#elements.sortSelect._choices = undefined;
        }
    }

    #attachEventListeners() {
        const {
            addButton,
            fileUploadInput,
            deleteButton,
            selectAllCheckbox,
            sortSelect,
            fileListContainer,
        } = this.#elements;

        this.#eventHandlers = {
            upload: this.#handleFileUpload.bind(this),
            delete: this.#handleDeleteClick.bind(this),
            selectAll: this.#handleSelectAllChange.bind(this),
            sort: (e) => this.#applySort(e.target.value),
            selectionChange: (e) =>
                e.target.matches('.file-select input[type="checkbox"]') &&
                this.#updateSelectionState(),
            reload: this.loadData.bind(this),
        };

        addButton?.addEventListener('click', () => fileUploadInput?.click());
        fileUploadInput?.addEventListener('change', this.#eventHandlers.upload);
        deleteButton?.addEventListener('click', this.#eventHandlers.delete);
        selectAllCheckbox?.addEventListener(
            'change',
            this.#eventHandlers.selectAll,
        );
        sortSelect?.addEventListener('change', this.#eventHandlers.sort);
        fileListContainer?.addEventListener(
            'change',
            this.#eventHandlers.selectionChange,
        );

        document.addEventListener(
            'photosUpdatedInModal',
            this.#eventHandlers.reload,
        );
    }

    /**
     * Public method to load or reload the gallery content.
     */
    async loadData() {
        this.#_refreshConfig(); // Ensure config is up-to-date before any action.

        if (
            !this.#config.getUrl ||
            !this.#elements.fileListContainer ||
            this.#config.getUrl.includes(PLACEHOLDER_ID_SEGMENT)
        ) {
            this.#renderFileList([]);
            return;
        }

        try {
            const data = await this.#fetchAPI(this.#config.getUrl);
            this.#renderFileList(data?.[this.#config.filesField] || []);
        } catch (error) {
            this.#renderFileList([]);
        } finally {
            this.#initializeContextMenus();
            this.#applySort(localStorage.getItem(SORT_STORAGE_KEY) || 'newest');
            this.#updateSelectionState();
        }
    }

    #renderFileList(files) {
        const container = this.#elements.fileListContainer;
        if (!container) return;

        container.innerHTML = '';

        if (!Array.isArray(files) || files.length === 0) {
            container.innerHTML = `<p class="empty">${gettext('Brak plików.')}</p>`;
            return;
        }

        const fragment = document.createDocumentFragment();
        files.forEach((file) =>
            fragment.appendChild(this.#createFileElement(file)),
        );
        container.appendChild(fragment);
        initializePings(container);
    }

    #createFileElement(file) {
        const fileDiv = document.createElement('div');
        fileDiv.className = `file ${file.is_main ? 'is-main' : ''} ${file.is_offer ? 'is-offer' : ''}`;
        Object.assign(fileDiv.dataset, {
            id: file.id,
            name: file.name,
            time: file.upload_time,
            isMain: String(file.is_main),
            isOffer: String(file.is_offer),
        });

        const uploadDate = new Date(file.upload_time);
        const displayDate = `${uploadDate.toLocaleDateString()} ${uploadDate.toLocaleTimeString()}`;
        let thumbnailUrl = file.square_thumbnail;
        if (!thumbnailUrl) {
            thumbnailUrl = file.mime_type?.startsWith('image/')
                ? '/static/images/pic_fill.svg'
                : '/static/images/document.svg';
        }
        const thumbnailClass = file.square_thumbnail
            ? ''
            : 'thumbnail-placeholder';

        fileDiv.innerHTML = `
            <div class="file-thumbnail">
                <div class="photo-status-icons">
                    ${file.is_main ? `<img src="/static/images/main_offer.svg" title="${gettext('Główne zdjęcie oferty')}">` : ''}
                    ${file.is_offer && !file.is_main ? `<img src="/static/images/offer.svg" title="${gettext('Zdjęcie do oferty')}">` : ''}
                </div>
                <button class="btn-table-menu photo-menu-btn" aria-label="${gettext('Opcje zdjęcia')}">
                    <span class="icon-dots"></span>
                </button>
                <label class="file-select">
                    <input type="checkbox" name="file_select" value="${file.id}" />
                </label>
                <a href="${file.file}" target="_blank">
                    <img src="${thumbnailUrl}" class="${thumbnailClass}" alt="${gettext('Podgląd pliku')} ${file.name}" loading="lazy">
                </a>
                <div class="ping-container ping-button" data-update-key="document_${file.id}" data-updated-at="${file.upload_time}" data-ping-parent="${this.#config.pingButtonId}">
                    <span class="ping hidden"></span>
                </div>
            </div>
            <div class="file-data">
                <p title="${file.name}">${truncateString(file.name, 28)}</p>
                <p><small>${gettext('Data')}: ${displayDate}</small></p>
            </div>
        `;

        const fileLink = fileDiv.querySelector('a');
        const pingButton = fileDiv.querySelector('.ping-button');
        if (fileLink && pingButton) {
            fileLink.addEventListener('click', () => {
                handlePingSeen(pingButton);
            });
        }

        return fileDiv;
    }

    #initializeContextMenus() {
        this.#element.querySelectorAll('.photo-menu-btn').forEach((button) => {
            if (button.dataset.menuInitialized) return;
            button.dataset.menuInitialized = 'true';

            button.addEventListener('click', (e) => {
                const fileDiv = button.closest('.file');
                const fileId = fileDiv.dataset.id;
                const menu = this.#buildContextMenu(fileDiv, fileId);
                showContextMenu(menu, e);
            });
        });
    }

    #buildContextMenu(fileDiv, fileId) {
        const menu = [];
        this.#_refreshConfig();
        const getUrl = this.#config.getUrl;

        if (this.#config.filesField === 'photos') {
            const componentId = getUrl?.match(/components\/(\d+)\//)?.[1];
            if (componentId) {
                const isMain = fileDiv.dataset.isMain === 'true';
                const isOffer = fileDiv.dataset.isOffer === 'true';

                if (isMain) {
                    menu.push(
                        { label: gettext('Ustaw jako główne'), disabled: true },
                        {
                            label: gettext('Odznacz z oferty'),
                            action: () =>
                                updatePhotos(componentId, [
                                    {
                                        id: fileId,
                                        is_offer: false,
                                        is_main: false,
                                    },
                                ]),
                        },
                    );
                } else {
                    menu.push(
                        {
                            label: gettext('Ustaw jako główne'),
                            action: () =>
                                updatePhotos(componentId, [
                                    { id: fileId, is_main: true },
                                ]),
                        },
                        {
                            label: isOffer
                                ? gettext('Odznacz z oferty')
                                : gettext('Zaznacz do oferty'),
                            action: () =>
                                updatePhotos(componentId, [
                                    { id: fileId, is_offer: !isOffer },
                                ]),
                        },
                    );
                }
            }
        }
        menu.push({
            label: gettext('Usuń'),
            action: () => this.#handleSingleDelete(fileId),
        });
        return menu;
    }

    async #handleFileUpload(event) {
        const files = event.target.files;
        this.#_refreshConfig();
        if (!files?.length || !this.#config.addUrl) return;

        const formData = new FormData();
        [...files].forEach((file) => formData.append('files', file));

        this.#elements.addButton?.setAttribute('disabled', 'true');

        try {
            const uploadedFiles = await this.#fetchAPI(
                UPLOAD_URL,
                'POST',
                formData,
            );
            const uploadedIds = uploadedFiles?.map((file) => file.id);
            if (!uploadedIds?.length)
                throw new Error(
                    gettext('Nie udało się przetworzyć przesłanych plików.'),
                );

            const addResponse = await this.#fetchAPI(
                this.#config.addUrl,
                'POST',
                { ids: uploadedIds },
            );
            if (addResponse !== null) {
                notifySuccess(gettext('Pliki zostały dodane pomyślnie.'));
                dispatchGalleryUpdateEvents();
            }
        } finally {
            this.#elements.addButton?.removeAttribute('disabled');
            if (event.target) event.target.value = null;
        }
    }

    async #handleDeleteClick() {
        const ids = this.#getSelectedFileIds();
        this.#_refreshConfig();
        if (!ids.length || !this.#config.removeUrl) return;

        const { isConfirmed } = await prompt({
            text: gettext('Czy na pewno chcesz usunąć zaznaczone pliki?'),
        });
        if (!isConfirmed) return;

        this.#elements.deleteButton?.setAttribute('disabled', 'true');
        this.#elements.selectAllCheckbox?.setAttribute('disabled', 'true');

        try {
            const response = await this.#fetchAPI(
                this.#config.removeUrl,
                'POST',
                { ids },
            );
            if (response !== null) {
                notifySuccess(gettext('Pliki zostały usunięte.'));
                dispatchGalleryUpdateEvents();
            }
        } finally {
            this.#elements.selectAllCheckbox?.removeAttribute('disabled');
            this.#updateSelectionState();
        }
    }

    async #handleSingleDelete(fileId) {
        this.#_refreshConfig();
        if (!fileId || !this.#config.removeUrl) return;

        const { isConfirmed } = await prompt({
            text: gettext('Czy na pewno chcesz usunąć ten plik?'),
        });
        if (!isConfirmed) return;

        const response = await this.#fetchAPI(this.#config.removeUrl, 'POST', {
            ids: [fileId],
        });
        if (response !== null) {
            notifySuccess(gettext('Plik został usunięty.'));
            dispatchGalleryUpdateEvents();

            if (this.#config.pingButtonId && response.documents_updated) {
                const documentsBtn = document.getElementById(
                    this.#config.pingButtonId,
                );
                if (documentsBtn) {
                    documentsBtn.dataset.updatedAt = response.documents_updated;
                    updatePing(documentsBtn);
                }
            }
        }
    }

    #handleSelectAllChange() {
        const checked = this.#elements.selectAllCheckbox?.checked || false;
        this.#elements.fileListContainer
            ?.querySelectorAll('.file-select input[type="checkbox"]')
            .forEach((cb) => (cb.checked = checked));
        this.#updateSelectionState();
    }

    #applySort(criteria) {
        const container = this.#elements.fileListContainer;
        if (!container || !criteria) return;

        const files = Array.from(container.querySelectorAll('.file'));
        if (!files.length) return;

        const compare =
            {
                oldest: (a, b) =>
                    new Date(a.dataset.time) - new Date(b.dataset.time),
                az: (a, b) => a.dataset.name.localeCompare(b.dataset.name),
                za: (a, b) => b.dataset.name.localeCompare(a.dataset.name),
                newest: (a, b) =>
                    new Date(b.dataset.time) - new Date(a.dataset.time),
            }[criteria] ||
            ((a, b) => new Date(b.dataset.time) - new Date(a.dataset.time));

        files.sort(compare).forEach((f) => container.appendChild(f));
        if (this.#elements.sortSelect) {
            this.#elements.sortSelect.value = criteria;
        }
        localStorage.setItem(SORT_STORAGE_KEY, criteria);
    }

    #getSelectedFileIds() {
        return Array.from(
            this.#elements.fileListContainer?.querySelectorAll(
                '.file-select input:checked',
            ) || [],
        ).map((cb) => cb.value);
    }

    #updateSelectionState() {
        const { fileListContainer, selectAllCheckbox, deleteButton } =
            this.#elements;
        if (!fileListContainer) return;

        const allCheckboxes = fileListContainer.querySelectorAll(
            '.file-select input[type="checkbox"]',
        );
        const checked = fileListContainer.querySelectorAll(
            '.file-select input:checked',
        );
        const hasFiles = allCheckboxes.length > 0;
        const anySelected = checked.length > 0;
        const allSelected = hasFiles && checked.length === allCheckboxes.length;

        if (selectAllCheckbox) {
            selectAllCheckbox.checked = allSelected;
            selectAllCheckbox.indeterminate = anySelected && !allSelected;
            selectAllCheckbox.disabled = !hasFiles;
        }
        if (deleteButton) deleteButton.disabled = !anySelected;
    }

    async #fetchAPI(url, method = 'GET', body = null) {
        const headers = { 'X-CSRFToken': getCookie('csrftoken') };
        const options = { method, headers };

        if (body) {
            if (body instanceof FormData) {
                options.body = body;
            } else {
                headers['Content-Type'] = 'application/json';
                options.body = JSON.stringify(body);
            }
        }

        try {
            const response = await fetch(url, options);
            if (response.status === 204) return {};
            const data = await response.json();
            if (!response.ok) {
                throw new Error(
                    data?.message ||
                        data?.error ||
                        JSON.stringify(data) ||
                        response.statusText,
                );
            }
            return data;
        } catch (error) {
            console.error(`API Error (${method} ${url}):`, error);
            notifyError(
                error.message ||
                    gettext('Coś poszło nie tak. Spróbuj ponownie.'),
            );
            return null;
        }
    }
}

// --- Global Initialization ---
document.addEventListener('bundleLoaded', () => {
    document.querySelectorAll('.file-gallery').forEach((el) => {
        if (!el.dataset.galleryInitialized) {
            el.dataset.galleryInitialized = 'true';
            el.galleryInstance = new FileGallery(el);
        }
    });
});
